import { useEffect, type ReactNode } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeft, Save } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { useCurrentUserProfile } from '@/hooks/useCurrentUserProfile'
import { useNotifications } from '@/contexts/NotificationContext'
import { paths } from '@/routes/path'

const profileSchema = z.object({
  firstName: z
    .string()
    .min(2, 'First name must be at least 2 characters')
    .max(60, 'First name must be 60 characters or less'),
  lastName: z
    .string()
    .min(2, 'Last name must be at least 2 characters')
    .max(60, 'Last name must be 60 characters or less'),
  phone: z
    .string()
    .optional()
    .transform((value) => (value?.trim() ? value.trim() : '')),
  bio: z
    .string()
    .max(280, 'Bio must be 280 characters or less')
    .optional()
    .transform((value) => (value?.trim() ? value.trim() : '')),
})

type ProfileFormValues = z.infer<typeof profileSchema>

export function UserProfileEditPage() {
  const navigate = useNavigate()
  const { addNotification } = useNotifications()
  const { profile, userId, isLoading, isFetching, updateProfile, isUpdating } = useCurrentUserProfile()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      phone: '',
      bio: '',
    },
  })

  useEffect(() => {
    if (profile) {
      reset({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        phone: profile.phone || '',
        bio: profile.bio || '',
      })
    }
  }, [profile, reset])

  const onSubmit = handleSubmit(async (values) => {
    if (!userId) return

    try {
      await updateProfile({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        phone: values.phone ? values.phone : null,
        bio: values.bio ? values.bio : null,
      })

      addNotification({
        title: 'Profile updated',
        message: 'Your profile information has been saved successfully.',
        type: 'general',
        priority: 'low',
        isRead: false,
        isArchived: false,
      })

      navigate(paths.app.profile.absolute)
    } catch (mutationError) {
      const message =
        mutationError instanceof Error
          ? mutationError.message
          : 'We could not update your profile at this time.'

      addNotification({
        title: 'Update failed',
        message,
        type: 'general',
        priority: 'medium',
        isRead: false,
        isArchived: false,
      })
    }
  })

  if (!userId) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
        <p className="text-lg font-semibold text-secondary-900">No active user session</p>
        <p className="mt-1 max-w-md text-sm text-secondary-600">
          Sign in again to update your profile details.
        </p>
        <Button asChild variant="outline" className="mt-4">
          <Link to={paths.auth.login.absolute}>Go to login</Link>
        </Button>
      </div>
    )
  }

  if (isLoading || isFetching) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
        <p className="text-lg font-semibold text-secondary-900">We could not find your profile</p>
        <Button asChild variant="outline" className="mt-4">
          <Link to={paths.app.profile.absolute}>Return to profile</Link>
        </Button>
      </div>
    )
  }

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <Button
          type="button"
          variant="outline"
          className="w-full justify-center gap-2 text-secondary-700 sm:w-auto"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>

        <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
          <Button
            type="button"
            variant="ghost"
            className="w-full justify-center text-secondary-600 sm:w-auto"
            onClick={() => navigate(paths.app.profile.absolute)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="w-full items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 sm:w-auto"
            disabled={!isDirty || isSubmitting || isUpdating}
          >
            <Save className="h-4 w-4" />
            {isSubmitting || isUpdating ? 'Saving...' : 'Save changes'}
          </Button>
        </div>
      </div>

      <Card className="border border-secondary-100 shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-semibold text-secondary-900">
            Personal information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <FormField
              label="First name"
              error={errors.firstName?.message}
            >
              <Input placeholder="Enter your first name" {...register('firstName')} />
            </FormField>

            <FormField
              label="Last name"
              error={errors.lastName?.message}
            >
              <Input placeholder="Enter your last name" {...register('lastName')} />
            </FormField>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <FormField label="Email address">
              <Input value={profile.email || ''} readOnly disabled />
            </FormField>

            {/* <FormField label="Phone number" error={errors.phone?.message}>
              <Input
                placeholder="Add a phone number"
                {...register('phone')}
              />
            </FormField> */}
          </div>

          {/* <FormField label="Bio" error={errors.bio?.message}>
            <Textarea
              rows={4}
              placeholder="Tell us a bit about yourself (optional)"
              {...register('bio')}
            />
          </FormField> */}
        </CardContent>
      </Card>
    </form>
  )
}

type FormFieldProps = {
  label: string
  error?: string
  children: ReactNode
}

function FormField({ label, error, children }: FormFieldProps) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-semibold text-secondary-700">{label}</Label>
      {children}
      {error && <p className="text-sm text-rose-600">{error}</p>}
    </div>
  )
}
